import Service from '@ember/service';
import { tracked } from '@glimmer/tracking';
import config from 'uplisting-frontend/config/environment';

export type AirbnbGuestDataStep =
  | 'guest_identity'
  | 'safeguest'
  | 'contact_form'
  | 'rental_agreement';

export type AirbnbGuestDataException =
  | 'applicable_law'
  | 'community_rule'
  | 'allowed_contract'
  | 'optional_amenity'
  | 'luxe_listing'
  | 'other';

const PERSONAL_DATA_EXCEPTIONS: AirbnbGuestDataException[] = [
  'applicable_law',
  'community_rule',
  'allowed_contract',
  'other',
];

export const EXCEPTIONS_BY_STEP: Record<
  AirbnbGuestDataStep,
  AirbnbGuestDataException[]
> = {
  guest_identity: PERSONAL_DATA_EXCEPTIONS,
  safeguest: PERSONAL_DATA_EXCEPTIONS,
  contact_form: PERSONAL_DATA_EXCEPTIONS,
  rental_agreement: [
    'applicable_law',
    'community_rule',
    'optional_amenity',
    'luxe_listing',
    'other',
  ],
};

export interface IPendingAcknowledgement {
  step: AirbnbGuestDataStep;
  proceed: () => Promise<void>;
}

export default class AirbnbGuestDataPolicyService extends Service {
  policyFrom = new Date(config.airbnbOfficial.guestDataPolicyFrom);

  @tracked pending?: IPendingAcknowledgement;

  get isActive(): boolean {
    return Date.now() >= this.policyFrom.getTime();
  }

  async guard(
    step: AirbnbGuestDataStep | undefined,
    wasOn: boolean,
    proceed: () => Promise<void>,
  ): Promise<void> {
    if (step && !wasOn && this.isActive) {
      this.pending = { step, proceed };

      return;
    }

    await proceed();
  }

  async proceed(entry: IPendingAcknowledgement): Promise<void> {
    if (this.pending === entry) {
      this.pending = undefined;
    }

    await entry.proceed();
  }

  cancel(): void {
    this.pending = undefined;
  }
}

declare module '@ember/service' {
  interface Registry {
    'airbnb-guest-data-policy': AirbnbGuestDataPolicyService;
  }
}
